import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { api, type Review } from '../lib/api'

export function StarsRow({ n }: { n: number }) {
  return (
    <span className="buykey-stars" aria-label={`${n} sao`}>
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`fa-${i < n ? 'solid' : 'regular'} fa-star${i < n ? ' on' : ''}`}
          aria-hidden
        />
      ))}
    </span>
  )
}

export function formatReviewTime(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

let templatesCache: Promise<string[]> | null = null

function loadTemplates() {
  templatesCache ??= api
    .replyTemplates()
    .then((r) => r.templates)
    .catch(() => {
      templatesCache = null
      return []
    })
  return templatesCache
}

function ReplyEditor({
  review,
  onSaved,
  onCancel,
}: {
  review: Review
  onSaved: (reply: string | null) => void
  onCancel: () => void
}) {
  const [text, setText] = useState(review.admin_reply || '')
  const [templates, setTemplates] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    loadTemplates().then(setTemplates)
  }, [])

  async function save() {
    setBusy(true)
    setErr('')
    try {
      const r = await api.replyReview(review.id, text)
      onSaved(r.admin_reply)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Lỗi lưu')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="review-reply-editor">
      {templates.length ? (
        <div className="review-reply-templates">
          {templates.map((t) => (
            <button key={t} type="button" className="review-reply-chip" onClick={() => setText(t)}>
              {t}
            </button>
          ))}
        </div>
      ) : null}
      <textarea
        className="review-reply-area"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder="Phản hồi khách…"
        autoFocus
      />
      {err ? <p className="note">{err}</p> : null}
      <div className="review-reply-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
          Hủy
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
          <i className="fa-solid fa-paper-plane" aria-hidden /> {busy ? 'Đang lưu…' : 'Gửi'}
        </button>
      </div>
    </div>
  )
}

type ReplyProps = {
  editing: number | null
  setEditing: (id: number | null) => void
  onReplied: (id: number, reply: string | null) => void
}

function ReviewReply({ r, editing, setEditing, onReplied }: ReplyProps & { r: Review }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  if (editing === r.id) {
    return (
      <ReplyEditor
        review={r}
        onCancel={() => setEditing(null)}
        onSaved={(reply) => {
          onReplied(r.id, reply)
          setEditing(null)
        }}
      />
    )
  }
  return (
    <>
      {r.admin_reply ? (
        <div className="buykey-admin-reply">
          <span>
            <i className="fa-solid fa-reply" aria-hidden /> Admin
          </span>
          <p>{r.admin_reply}</p>
        </div>
      ) : null}
      {isAdmin ? (
        <button type="button" className="review-reply-btn" onClick={() => setEditing(r.id)}>
          <i className={`fa-solid ${r.admin_reply ? 'fa-pen' : 'fa-reply'}`} aria-hidden />{' '}
          {r.admin_reply ? 'Sửa phản hồi' : 'Trả lời'}
        </button>
      ) : null}
    </>
  )
}

export function ReviewTimeline({
  items,
  onReplied,
}: {
  items: Review[]
  onReplied: (id: number, reply: string | null) => void
}) {
  const [editing, setEditing] = useState<number | null>(null)

  return (
    <ol className="buykey-timeline">
      {items.map((r) => (
        <li key={r.id} className="buykey-tl-item">
          <time dateTime={r.created_at || undefined}>{formatReviewTime(r.created_at)}</time>
          <div className="buykey-tl-card">
            <div className="buykey-tl-head">
              <strong>{r.user_name}</strong>
              <StarsRow n={r.stars} />
            </div>
            <p>{r.content}</p>
            <ReviewReply r={r} editing={editing} setEditing={setEditing} onReplied={onReplied} />
          </div>
        </li>
      ))}
    </ol>
  )
}

/** Lưới masonry — nhiều đánh giá trong một màn hình */
export function ReviewWall({
  items,
  onReplied,
}: {
  items: Review[]
  onReplied: (id: number, reply: string | null) => void
}) {
  const [editing, setEditing] = useState<number | null>(null)

  return (
    <div className="review-wall">
      {items.map((r) => (
        <article key={r.id} className="review-card">
          <div className="buykey-tl-head">
            <strong>{r.user_name}</strong>
            <StarsRow n={r.stars} />
          </div>
          <p className="review-card-text">{r.content}</p>
          <time className="review-card-time" dateTime={r.created_at || undefined}>
            {formatReviewTime(r.created_at)}
          </time>
          <ReviewReply r={r} editing={editing} setEditing={setEditing} onReplied={onReplied} />
        </article>
      ))}
    </div>
  )
}

export function ReviewPager({
  page,
  totalPages,
  disabled,
  onPage,
}: {
  page: number
  totalPages: number
  disabled?: boolean
  onPage: (p: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div className="buykey-pager">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={page <= 1 || disabled}
        onClick={() => onPage(Math.max(1, page - 1))}
      >
        <i className="fa-solid fa-chevron-left" aria-hidden /> Trước
      </button>
      <span className="muted">
        Trang {page}/{totalPages}
      </span>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={page >= totalPages || disabled}
        onClick={() => onPage(Math.min(totalPages, page + 1))}
      >
        Sau <i className="fa-solid fa-chevron-right" aria-hidden />
      </button>
    </div>
  )
}
